export const STATUS = {
  created: { label: 'Created', cls: 'bg-slate-500/10 text-slate-600 border-slate-500/30', dot: 'bg-slate-400' },
  pending_admin_review: { label: 'Pending', cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30', dot: 'bg-amber-500' },
  approved: { label: 'Approved', cls: 'bg-blue-500/10 text-blue-600 border-blue-500/30', dot: 'bg-blue-500' },
  processing: { label: 'Processing', cls: 'bg-violet-500/10 text-violet-600 border-violet-500/30', dot: 'bg-violet-500' },
  paid: { label: 'Completed', cls: 'bg-green-500/10 text-green-600 border-green-500/30', dot: 'bg-green-500' },
  failed: { label: 'Failed', cls: 'bg-red-500/10 text-red-600 border-red-500/30', dot: 'bg-red-500' },
  rejected: { label: 'Rejected', cls: 'bg-red-500/10 text-red-600 border-red-500/30', dot: 'bg-red-500' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-500/10 text-slate-500 border-slate-500/30', dot: 'bg-slate-400' },
  reversed: { label: 'Reversed', cls: 'bg-orange-500/10 text-orange-600 border-orange-500/30', dot: 'bg-orange-500' },
};
export const statusOf = (s) => STATUS[s] || STATUS.created;

// Status tabs -> backend status filter (comma list). Mirrors existing PayoutStatus enum.
export const TABS = [
  { key: 'all', label: 'All', statuses: null },
  { key: 'pending', label: 'Pending', statuses: ['pending_admin_review', 'created'] },
  { key: 'approved', label: 'Approved', statuses: ['approved'] },
  { key: 'processing', label: 'Processing', statuses: ['processing'] },
  { key: 'paid', label: 'Completed', statuses: ['paid'] },
  { key: 'rejected', label: 'Rejected', statuses: ['rejected'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
  { key: 'failed', label: 'Failed', statuses: ['failed', 'reversed'] },
];

// Valid single/bulk actions per status — mirrors PayoutService guards + BULK_ELIGIBLE on the backend.
export const ACTIONS = {
  approve: { label: 'Approve', from: ['pending_admin_review'], tone: 'success' },
  reject: { label: 'Reject', from: ['pending_admin_review'], tone: 'danger', needsReason: true },
  processing: { label: 'Mark Processing', from: ['approved', 'failed'], tone: 'primary' },
  paid: { label: 'Mark Completed', from: ['approved', 'processing'], tone: 'success', needsProof: true },
  cancel: { label: 'Cancel', from: ['created', 'pending_admin_review', 'approved', 'failed'], tone: 'danger', needsReason: true },
  retry: { label: 'Retry', from: ['failed'], tone: 'primary' },
  execute: { label: 'Auto Execute', from: ['approved'], tone: 'primary' },
  failed: { label: 'Mark Failed', from: ['processing'], tone: 'danger', needsReason: true },
};
export const actionsFor = (status) => Object.entries(ACTIONS).filter(([, a]) => a.from.includes(status)).map(([k]) => k);
export const BULK_ACTION_KEYS = ['approve', 'reject', 'processing', 'paid', 'cancel', 'retry'];

export const formatINR = (n, digits = 0) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: Math.max(digits, 2),
}).format(n || 0);

export const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
export const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
export const fmtDateTime = (iso) => iso ? `${fmtDate(iso)}, ${fmtTime(iso)}` : '—';
export const maskAcct = (m) => m ? `•••• ${String(m).slice(-4)}` : '—';

// Exact admin copy format (plain text). Separator only BETWEEN orders.
export const COPY_SEPARATOR = '----------------------------------------';
export const formatCopyItem = (i) => [
  `Order ID : ${i.order_id ?? ''}`,
  `Account Holder : ${i.account_holder_name ?? ''}`,
  `Bank Name : ${i.bank_name ?? ''}`,
  `Account Number : ${i.account_number ?? ''}`,
  `IFSC : ${i.ifsc_code ?? ''}`,
  `Amount : ${formatINR(i.amount)}`,
].join('\n');
export const formatCopyBlock = (items) => items.map(formatCopyItem).join(`\n\n${COPY_SEPARATOR}\n\n`);

export const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

// Fetch full (unmasked) details via the admin-only endpoint and copy. Never changes payout state.
export const copyPayoutDetails = async (api, payoutIds, toast) => {
  try {
    const r = await api.post('/payouts/admin/copy-details', { payoutIds });
    const items = r.data.items || [];
    if (!items.length) { toast.error('Unable to copy. Please try again.'); return false; }
    const ok = await copyText(formatCopyBlock(items));
    ok ? toast.success(items.length > 1 ? `${items.length} payout details copied` : 'Payout details copied') : toast.error('Unable to copy. Please try again.');
    return ok;
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Unable to copy. Please try again.');
    return false;
  }
};
